import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, BadgeCheck } from "lucide-react";
import { MaskedLines, FadeIn, EASE } from "@/components/Motion";
import ReelPlayer from "@/components/ReelPlayer";

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const yCard1 = useTransform(scrollYProgress, [0, 1], [0, -44]);
  const yCard2 = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const py = (v) => (reduce ? undefined : v);

  return (
    <section ref={ref} className="relative overflow-hidden pt-[110px] lg:pt-[140px]" data-testid="home-hero-section">
      <div aria-hidden className="pointer-events-none absolute -top-40 right-[-12%] h-[560px] w-[560px] rounded-full bg-ember/15 blur-[140px]" />
      <div aria-hidden className="pointer-events-none absolute left-[-15%] top-[45%] h-[420px] w-[420px] rounded-full bg-gold/10 blur-[130px]" />

      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10 lg:pb-28">
        <div>
          <FadeIn delay={0.1}>
            <p className="eyebrow mb-6 flex items-center gap-3">
              <span aria-hidden className="h-px w-8 bg-gold/60" />
              Family-owned custom creation shop
            </p>
          </FadeIn>

          <MaskedLines
            lines={["Custom creations,", "from thread to", "laser to layer."]}
            lineClassName="font-display text-[clamp(2.5rem,7vw,4.8rem)] font-extrabold leading-[1.05] tracking-tight text-white"
            delay={0.18}
          />

          <FadeIn delay={0.55}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-mist sm:text-lg">
              3D printing, laser engraving, embroidery, DTF printing, sublimation, custom apparel and personalized
              gifts — made one order at a time in our family workshop.
            </p>
          </FadeIn>

          <FadeIn delay={0.7}>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link to="/quote" data-testid="hero-request-quote-button" className="btn-primary">
                Request a Quote <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <Link to="/work" data-testid="hero-view-work-button" className="btn-ghost">
                View Our Work <ArrowUpRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </FadeIn>

          <FadeIn delay={0.85}>
            <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.22em] text-mist/80">
              Individuals · Businesses · Schools · Teams &amp; Groups
            </p>
          </FadeIn>
        </div>

        {/* Parallax composition of real work */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
          className="relative h-[400px] sm:h-[500px] lg:h-[540px]"
          data-testid="home-hero-gallery"
        >
          <motion.div
            style={{ y: py(yMain) }}
            className="absolute left-0 top-8 w-[78%] overflow-hidden rounded-2xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]"
          >
            <ReelPlayer
              src="/videos/embroidery-reel.mp4"
              srcWebm="/videos/embroidery-reel.webm"
              poster="/images/embroidery-reel-poster.jpg"
              label="Machine embroidery in progress — white thread stitching the word 'Luxury' onto vibrant blue fabric"
              testId="hero-embroidery-reel"
              className="aspect-video"
            />
            <span className="absolute bottom-3 left-3 rounded-full bg-ink/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-gold backdrop-blur">
              Real work — machine embroidery
            </span>
          </motion.div>

          <motion.div
            style={{ y: py(yCard2) }}
            className="absolute bottom-0 right-[4%] w-[48%] animate-floaty-slow overflow-hidden rounded-2xl border border-white/10 shadow-2xl"
          >
            <img
              src="/images/work-dtf-rolls.jpg"
              alt="Rolls of colorful freshly printed DTF transfers ready to press"
              className="aspect-[16/10] w-full object-cover"
            />
          </motion.div>

          <motion.div
            style={{ y: py(yCard1) }}
            className="absolute bottom-6 left-2 flex items-center gap-2 rounded-full border border-white/10 bg-panel/90 px-4 py-2.5 backdrop-blur"
          >
            <BadgeCheck className="h-4 w-4 text-gold" aria-hidden />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/80">Made in-house</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
